import { api } from "@/lib/api";
import type { ConstructorSessionSummary, PageResponse } from "@/types/api";
import type {
    CreateDraftRequest,
    DraftAssetDto,
    DraftDto,
    DraftFileContentDto,
    DraftFileDto,
    PublishedVersionDto,
} from "@/types/constructor";

const BASE = "/admin/constructor";

/** Draft lifecycle states the list endpoint can filter by. */
export type DraftStatusFilter = "active" | "published" | "discarded";

export interface ListDraftsParams {
    /** Advertiser filter. -1 means "IS NULL" (unassigned). Omitted = no filter. */
    advertiserId?: number;
    /** Lifecycle filter, applied server-side. Omitted = every status. */
    status?: DraftStatusFilter;
}

export async function listDrafts(params: ListDraftsParams = {}): Promise<DraftDto[]> {
    const q: Record<string, string | number> = { size: 200 };
    if (params.advertiserId != null) q.advertiserId = params.advertiserId;
    if (params.status) q.status = params.status;
    const { data } = await api.get<{ content: DraftDto[] }>(`${BASE}/drafts`, { params: q });
    return data.content || [];
}

export async function createDraft(req: CreateDraftRequest): Promise<DraftDto> {
    const { data } = await api.post<DraftDto>(`${BASE}/drafts`, req);
    return data;
}

export async function uploadDraft(zip: File): Promise<DraftDto> {
    const form = new FormData();
    form.append("bundle", zip);
    const { data } = await api.post<DraftDto>(`${BASE}/drafts/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

export async function getDraft(id: number): Promise<DraftDto> {
    const { data } = await api.get<DraftDto>(`${BASE}/drafts/${id}`);
    return data;
}

export async function renameDraft(id: number, name: string): Promise<DraftDto> {
    const { data } = await api.patch<DraftDto>(`${BASE}/drafts/${id}`, { name });
    return data;
}

export async function discardDraft(id: number): Promise<void> {
    await api.post(`${BASE}/drafts/${id}/discard`);
}

/** Bring a discarded draft back to active. Backend 409s if the draft isn't discarded. */
export async function restoreDraft(id: number): Promise<void> {
    await api.post(`${BASE}/drafts/${id}/restore`);
}

/** Hard-delete a discarded draft + its R2 files. Backend 409s unless the draft is discarded. */
export async function purgeDraft(id: number): Promise<void> {
    await api.delete(`${BASE}/drafts/${id}/purge`);
}

/**
 * Publish a draft. When the draft's advertiser owner differs from the
 * underlying page's, the backend refuses with HTTP 409 and a JSON body of
 * shape {@link import("@/types/commerce").CrossAdvertiserPublishError}.
 * Re-issue with `{ confirmOwnershipChange: true }` to overwrite the page's
 * advertiser (a row lands in `advertiser_assignment_log`).
 */
export async function publishDraft(
    id: number,
    options: { confirmOwnershipChange?: boolean } = {},
): Promise<PublishedVersionDto> {
    const { data } = await api.post<PublishedVersionDto>(
        `${BASE}/drafts/${id}/publish`,
        undefined,
        options.confirmOwnershipChange
            ? { params: { confirmOwnershipChange: true } }
            : undefined,
    );
    return data;
}

export async function listFiles(draftId: number): Promise<DraftFileDto[]> {
    const { data } = await api.get<DraftFileDto[]>(`${BASE}/drafts/${draftId}/files`);
    return data;
}

export async function getFile(draftId: number, path: string): Promise<DraftFileContentDto> {
    const { data } = await api.get<DraftFileContentDto>(`${BASE}/drafts/${draftId}/files/${path}`);
    return data;
}

export async function writeFile(
    draftId: number,
    path: string,
    content: string,
    expectedVersion?: number,
): Promise<DraftFileDto> {
    const { data } = await api.put<DraftFileDto>(`${BASE}/drafts/${draftId}/files/${path}`, {
        content,
        expectedVersion: expectedVersion ?? null,
    });
    return data;
}

export async function deleteFile(draftId: number, path: string): Promise<void> {
    await api.delete(`${BASE}/drafts/${draftId}/files/${path}`);
}

export async function moveFile(draftId: number, fromPath: string, toPath: string): Promise<DraftFileDto> {
    const { data } = await api.post<DraftFileDto>(`${BASE}/drafts/${draftId}/move`, { fromPath, toPath });
    return data;
}

export async function listAssets(draftId: number): Promise<DraftAssetDto[]> {
    const { data } = await api.get<DraftAssetDto[]>(`${BASE}/drafts/${draftId}/assets`);
    return data;
}

export async function uploadAsset(draftId: number, path: string, file: File): Promise<DraftAssetDto> {
    const form = new FormData();
    form.append("path", path);
    form.append("file", file);
    const { data } = await api.post<DraftAssetDto>(`${BASE}/drafts/${draftId}/assets`, form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

export async function deleteAsset(draftId: number, path: string): Promise<void> {
    await api.delete(`${BASE}/drafts/${draftId}/assets/${path}`);
}

// Raw asset bytes — served with the asset's stored Content-Type, cookie-gated.
// Use directly as <img src>, <video src>, <object data>, etc.
export function assetContentUrl(draftId: number, path: string): string {
    return `${BASE}/drafts/${draftId}/asset-content?path=${encodeURIComponent(path)}`;
}

// ---- Screenshots (persisted in R2 indefinitely, served via auth-gated proxy)

export interface ScreenshotRecord {
    id: string;
    draftId: number;
    sessionId?: number;
    capturedAt?: string;
    url: string;
}

export async function listScreenshots(draftId: number): Promise<ScreenshotRecord[]> {
    const { data } = await api.get<ScreenshotRecord[]>(`${BASE}/drafts/${draftId}/screenshots`);
    return data;
}

export function screenshotUrl(draftId: number, shotId: string): string {
    return `${BASE}/drafts/${draftId}/screenshots/${shotId}`;
}

// ---- Sessions ---------------------------------------------------------

export interface ConstructorSession {
    id: number;
    draftId: number;
    status: string;
    model: string;
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsdMicros: number;
    imageCount: number;
    startedAt: string;
    lastActivityAt: string;
}

export interface ConstructorTurn {
    id: number;
    seq: number;
    role: "user" | "assistant" | "tool" | "system";
    content: string | null;
    toolName: string | null;
    toolCallId: string | null;
    toolArgs: string | null;
    toolResult: string | null;
    inputTokens: number | null;
    outputTokens: number | null;
    createdAt: string;
    /** Sender of a role='user' turn. Null for assistant/tool/system. */
    userId?: number | null;
    /** Resolved sender email — only present for user turns. */
    authorEmail?: string | null;
    /** Media the user attached to a user turn (images / videos / pdfs / etc.). */
    attachments?: ChatAttachment[];
}

export interface ChatAttachment {
    id: number;
    sessionId: number;
    turnId: number | null;
    mime: string;
    originalName: string;
    sizeBytes: number;
    width?: number;
    height?: number;
    /** Authenticated raw URL — set Cookies: same-site on this image src. */
    url: string;
    createdAt: string;
}

/**
 * Global session list across every draft — the out-of-chat visibility +
 * kill surface. Optional status filter ("running" | "idle" | "error" |
 * "cancelled"). Sorted by last activity, newest first.
 */
export async function listSessions(
    status?: string,
    page?: number,
    size?: number,
): Promise<PageResponse<ConstructorSessionSummary>> {
    const params: Record<string, string | number> = {};
    if (status) params.status = status;
    if (page != null) params.page = page;
    if (size != null) params.size = size;
    const { data } = await api.get<PageResponse<ConstructorSessionSummary>>(`${BASE}/sessions`, { params });
    return data;
}

export async function openSession(draftId: number): Promise<ConstructorSession> {
    const { data } = await api.post<ConstructorSession>(`${BASE}/drafts/${draftId}/sessions`);
    return data;
}

export async function sendMessage(sessionId: number, content: string, attachmentIds: number[] = []): Promise<void> {
    await api.post(`${BASE}/sessions/${sessionId}/messages`, { content, attachmentIds });
}

export async function cancelSession(sessionId: number): Promise<{ stopped: boolean }> {
    const { data } = await api.post<{ stopped: boolean }>(`${BASE}/sessions/${sessionId}/cancel`);
    return data;
}

export async function uploadChatAttachment(sessionId: number, file: File): Promise<ChatAttachment> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post<ChatAttachment>(`${BASE}/sessions/${sessionId}/attachments`, form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

export async function listChatAttachments(sessionId: number): Promise<ChatAttachment[]> {
    const { data } = await api.get<ChatAttachment[]>(`${BASE}/sessions/${sessionId}/attachments`);
    return data;
}

export async function listTurns(sessionId: number, since = 0): Promise<ConstructorTurn[]> {
    const { data } = await api.get<ConstructorTurn[]>(`${BASE}/sessions/${sessionId}/turns`, {
        params: { since },
    });
    return data;
}
